import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Div, Image, Input, Row, Text } from '@pinpoint/design-kit';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import CONSTANTS from 'src/store/constants';
import { appActions, authActions } from 'src/store/actions';
import { ErrorText, Button } from 'src/components';

function ForgotPassword({ history, forgotPasswordStatus, forgotPasswordError, forgotPassword, enqueueNotification }) {
  const defaultValues = {
    username: '',
  };

  const { handleSubmit, errors, control } = useForm({ defaultValues });

  const [errorMessage, setErrorMessage] = useState('');

  const onSubmit = useCallback(
    (data) => {
      setErrorMessage('');
      const { username } = data;
      forgotPassword(username);
      enqueueNotification(
        'If our records have a matching account, an email will be sent to the address we have on file.'
      );
    },
    [forgotPassword, enqueueNotification]
  );

  useEffect(() => {
    // NOTE: To obfuscate the existence of accounts this API call
    // will return 202 Accepted, then redirect to change
    // password, failures will return generic 500 errors
    if (forgotPasswordStatus === CONSTANTS.API.SUCCEEDED) {
      history.push('/change-password');
    } else if (forgotPasswordStatus === CONSTANTS.API.FAILED) {
      setErrorMessage(forgotPasswordError);
    }
  }, [forgotPasswordStatus, forgotPasswordError, history]);

  return (
    <Div
      minW="100vw"
      w="100%"
      minH="100vh"
      h="100%"
      d="flex"
      justify="center"
      align="center"
      p={{ x: '1rem', y: '4rem' }}
      pos="relative"
    >
      <Image
        src="/static/login-back.png"
        srcSet="/static/login-back@2x.png 2x,/static/login-back@3x.png 3x,"
        alt="background"
        w="100vw"
        h="100vh"
        pos="absolute"
        top="0"
        style={{
          objectFit: 'cover',
        }}
      />
      <Div
        w="420px"
        d="flex"
        flexDirection="column"
        align="center"
        shadow="5"
        p={{ x: '2rem', t: '3rem', b: '1rem' }}
        rounded="sm"
        bg="black75"
        zIndex="10"
        style={{
          backdropFilter: 'blur(8px)',
        }}
      >
        <Container>
          <Div d="flex" justify="center" align="center">
            <Image src="/static/on-point-login.svg" w="108px" h="78px" />
          </Div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <Row p={{ t: '2rem' }}>
              <Col size={{ xs: '12' }} d="flex" justify="center">
                <Text textSize="subtitle" textColor="gray400">
                  Please enter your username or email.
                </Text>
              </Col>
            </Row>
            <Row p={{ y: '1rem' }} d="flex" justify="center">
              <Col>
                <Div d="flex" align="center">
                  <ErrorText shake={errorMessage !== ''}>{errorMessage}</ErrorText>
                </Div>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Controller
                  name="username"
                  as={Input}
                  placeholder="Username / Email"
                  control={control}
                  rules={{
                    required: 'Username or email is required',
                  }}
                  onKeyDown={(event) => {
                    if (event.keyCode === 13) {
                      handleSubmit(onSubmit)(event);
                    }
                  }}
                  rounded="sm"
                  p={{ y: '1em', x: '1.5em' }}
                  h="2.4rem"
                  textSize="input"
                />
              </Col>
            </Row>
            <Row>
              <Col size={{ xs: '12' }}>
                <Text textColor="danger600" textSize="caption">
                  <ErrorMessage errors={errors} name="username" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Div d="flex" justify="center">
                  <Button
                    buttonStyle="login"
                    onClick={(event) => {
                      handleSubmit(onSubmit)(event);
                    }}
                    onKeyDown={(event) => {
                      event.preventDefault();
                      if (event.keyCode === 13) {
                        handleSubmit(onSubmit)(event);
                      }
                    }}
                    isLoading={forgotPasswordStatus === CONSTANTS.API.STARTED}
                  >
                    Forgot Password
                  </Button>
                </Div>
              </Col>
            </Row>
          </form>
          <Div d="flex" justify="space-between" align="center">
            <Div p={{ y: '1rem' }} d="flex" justify="center">
              <Image w="90px" h="2rem" src="/static/powered-by-pinpoint-white.svg" />
            </Div>
            <Div h="1.5rem" border={{ r: '1px solid' }} borderColor="gray400" />
            <Div p={{ y: '1rem' }} d="flex" justify="center">
              <Text
                textDecor="underline"
                textSize="caption"
                textColor="gray400"
                cursor="pointer"
                onClick={() => {
                  window.open('https://pinpointpredictive.com/tos/', '_blank') ||
                    window.location.replace('https://pinpointpredictive.com/tos/');
                }}
              >
                Terms of Service
              </Text>
            </Div>
            <Div h="1.5rem" border={{ r: '1px solid' }} borderColor="gray400" />
            <Div p={{ y: '1rem' }} d="flex" justify="center">
              <Text
                textDecor="underline"
                textSize="caption"
                textColor="gray400"
                cursor="pointer"
                onClick={() => {
                  history.push('/login');
                }}
              >
                Login
              </Text>
            </Div>
          </Div>
        </Container>
      </Div>
    </Div>
  );
}

const mapStateToProps = ({ auth }) => ({
  ...auth,
});

const mapDispatchToProps = (dispatch) => ({
  login: (...params) => dispatch(authActions.login(...params)),
  forgotPassword: (...params) => dispatch(authActions.forgotPassword(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

ForgotPassword.propTypes = {
  forgotPasswordStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  forgotPasswordError: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  forgotPassword: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ForgotPassword);
